import { DqLoader } from "@decentriq/components";
import { usePublishedMediaDataRoomQuery } from "@decentriq/graphql/dist/hooks";
import { DataRoomKind } from "@decentriq/graphql/dist/types";
import { memo, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { type DataRoomContextValue, DataRoomProvider } from "contexts";
import {
  MediaDataRoomMainBar,
  NonSupportedMediaDataRoomAlert,
} from "features/mediaDataRoom/components";
import {
  MediaDataRoomAbWrapper,
  MediaDataRoomContextConsumer,
  MediaDataRoomHandlersAbWrapper,
  MediaDataRoomHandlersV3Wrapper,
  MediaDataRoomInsightsDataAbWrapper,
  MediaDataRoomInsightsDataV3Wrapper,
  MediaDataRoomV3Wrapper,
} from "features/mediaDataRoom/contexts";
import {
  AudiencesAbWrapper,
  AudiencesDataPrefetchingAbWrapper,
  AudiencesDataPrefetchingV3Wrapper,
  AudiencesV3Wrapper,
} from "features/mediaDataRoom/wrappers";
import MediaDataRoomAbErrorReport from "../MediaDataRoomErrorReport/MediaDataRoomAbErrorReport";
import MediaDataRoomV3ErrorReport from "../MediaDataRoomErrorReport/MediaDataRoomV3ErrorReport";
import MediaDataRoomActiveTab from "./MediaDataRoomActiveTab";

export interface MediaDataRoomProps {
  dataRoomId: string;
}

const MediaDataRoom = memo<MediaDataRoomProps>(({ dataRoomId }) => {
  const { data, loading, error } = usePublishedMediaDataRoomQuery({
    variables: { id: dataRoomId },
  });
  const kind = data?.publishedMediaDataRoom?.kind;
  const hasError = Boolean(error);
  const hasData = Boolean(data);
  const isLoading = loading;
  const dataRoomContextValue = useMemo<DataRoomContextValue>(
    () => ({
      allowTestMode: false,
      dataRoomId: dataRoomId || "__default__",
      isPublished: true,
    }),
    [dataRoomId]
  );
  if (isLoading) {
    return (
      <DqLoader
        sx={{
          alignItems: "center",
          height: "100%",
          justifyContent: "center",
          width: "100%",
        }}
      />
    );
  }
  if (hasError || !hasData) {
    return <Navigate replace={true} to="/datarooms" />;
  }
  const mediaDataRoom = data!.publishedMediaDataRoom;
  if (kind === DataRoomKind.MediaV3) {
    return (
      <DataRoomProvider value={dataRoomContextValue}>
        <MediaDataRoomV3ErrorReport
          createdAt={mediaDataRoom.createdAt}
          dataRoomId={mediaDataRoom.id}
        >
          <MediaDataRoomV3Wrapper data={mediaDataRoom}>
            <MediaDataRoomInsightsDataV3Wrapper>
              <MediaDataRoomHandlersV3Wrapper>
                <MediaDataRoomMainBar />
                <MediaDataRoomContextConsumer>
                  {(mediaDataRoomContextValue) => {
                    if (
                      !mediaDataRoomContextValue?.supportedFeatures
                        ?.canViewAudiences
                    ) {
                      <MediaDataRoomActiveTab />;
                    }
                    return (
                      <AudiencesV3Wrapper>
                        <AudiencesDataPrefetchingV3Wrapper>
                          <MediaDataRoomActiveTab />
                        </AudiencesDataPrefetchingV3Wrapper>
                      </AudiencesV3Wrapper>
                    );
                  }}
                </MediaDataRoomContextConsumer>
              </MediaDataRoomHandlersV3Wrapper>
            </MediaDataRoomInsightsDataV3Wrapper>
          </MediaDataRoomV3Wrapper>
        </MediaDataRoomV3ErrorReport>
      </DataRoomProvider>
    );
  }
  if (mediaDataRoom.kind === DataRoomKind.AbMedia) {
    return (
      <DataRoomProvider value={dataRoomContextValue}>
        <MediaDataRoomAbErrorReport
          createdAt={mediaDataRoom.createdAt}
          dataRoomId={mediaDataRoom.id}
          driverAttestationHash={mediaDataRoom.driverAttestationHash}
        >
          <MediaDataRoomAbWrapper data={mediaDataRoom}>
            <MediaDataRoomInsightsDataAbWrapper>
              <MediaDataRoomHandlersAbWrapper>
                <MediaDataRoomMainBar />
                <MediaDataRoomContextConsumer>
                  {(mediaDataRoomContextValue) => {
                    if (
                      !mediaDataRoomContextValue?.supportedFeatures
                        ?.canViewAudiences
                    ) {
                      <MediaDataRoomActiveTab />;
                    }
                    return (
                      <AudiencesAbWrapper>
                        <AudiencesDataPrefetchingAbWrapper>
                          <MediaDataRoomActiveTab />
                        </AudiencesDataPrefetchingAbWrapper>
                      </AudiencesAbWrapper>
                    );
                  }}
                </MediaDataRoomContextConsumer>
              </MediaDataRoomHandlersAbWrapper>
            </MediaDataRoomInsightsDataAbWrapper>
          </MediaDataRoomAbWrapper>
        </MediaDataRoomAbErrorReport>
      </DataRoomProvider>
    );
  }
  return <NonSupportedMediaDataRoomAlert />;
});
MediaDataRoom.displayName = "MediaDataRoom";

export default MediaDataRoom;
